/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ManagementAccountInterFace,
  ManagementAccountRequestInterface,
  ManagementAccountResponseInterface,
} from '../../interface/revenue-management.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import revenueApi from '../../utils/api/revenue';

import { setNotificationMessage } from './NotificationManagementSlice';

interface ManageAccountInitialProperties {
  accounts: ManagementAccountInterFace[];
  limit: number;
  page: number;
  totalPages: number;
  totalResults: number;
  loading: boolean;
  filter: {};
  filters: CommonFilterProperties[];
  filterListLoading: boolean;
  isFilterUpdated: boolean;
  sortBy: string;
  sortField: string;
}

// a thunk to fetch accounts
export const fetchAllAccounts = createAsyncThunk<
  ManagementAccountResponseInterface,
  ManagementAccountRequestInterface
>(
  'revenue-management-account/fetchAllAccounts',
  async (data: ManagementAccountRequestInterface, { dispatch }) => {
    try {
      const response = await revenueApi.fetchAccounts(
        removeEmptyKeys({
          ...data,
          submissionStatus: data?.submissionStatus ? data?.submissionStatus : 'approved',
        }),
      );
      return response.data;
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message,
          code: error?.response?.data?.code,
          status: true,
        }),
      );
      throw error;
    }
  },
);

// a thunk to fetch devices filters
export const fetchRevenueManagementAccountFilters = createAsyncThunk<
  CommonFilterProperties[], // Return type
  void // No need for an argument, since we are not using the payload
>('revenue-management-account/fetchRevenueManagementAccountFilters', async (_, { dispatch }) => {
  try {
    const response = await revenueApi.fetchRevenueManagementAccountsListFilters();
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
    return updatedResponse;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

// Apply default filters
const today = new Date();
const yesterday = new Date(today);
yesterday.setDate(today.getDate() - 1);

/**
 * Filter date format func
 * @param date
 * @returns
 */

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: ManageAccountInitialProperties = {
  accounts: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filter: {},
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

// Create the slice
export const revenueSlice = createSlice({
  name: 'revenue-management-account',
  initialState,
  reducers: {
    /**
     * Update device management filters
     * @param state
     * @param action
     */
    updateRevenueManagementAccountFilters: (
      state,
      action: PayloadAction<FilterUpdateProperties>,
    ) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param state
     */
    disableRevenueManagementAccountFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * Set Account filter data function
     * @param state
     * @param action
     */
    setRevenueAccountFilterData: (state, action) => {
      state.filter = action.payload;
    },
    /**
     * Function to update single station in redux
     * @param state
     * @param action
     */
    updateSingleAccount: (
      state: ManageAccountInitialProperties,
      action: PayloadAction<ManagementAccountInterFace>,
    ) => {
      const updatedAccount = action.payload; // station to be added at the top
      const accountList = state.accounts; // Assign the existing station to another variable

      // Find the index of the updated station by ID
      const index = accountList.findIndex((eachStation) => eachStation.id === updatedAccount.id);

      // If the index found, replace the entire data
      if (index !== -1) {
        accountList[index] = {
          ...updatedAccount,
          revenuePercentage: action.payload.revenuePercentage,
        };
      }
      state.accounts = accountList; // Finally update the station list
    },
    /**
     * function for set sortBy
     * @param state
     * @param action
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param builder
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllAccounts.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllAccounts.fulfilled, (state, action) => {
        state.loading = false;
        state.accounts = action.payload?.results?.length
          ? action.payload?.results?.map((result) => ({
              ...result,
              id: result._id,
              revenuePercentage: result.revenuePercentage ? Number(result.revenuePercentage) : 0,
            }))
          : [];
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      })
      .addCase(fetchAllAccounts.rejected, (state) => {
        state.loading = false;
        // Handle error if needed
      });
    builder
      .addCase(fetchRevenueManagementAccountFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchRevenueManagementAccountFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;

          state.filters = [...(state.filters || []), ...action.payload].filter(
            (object, index, self) => index === self.findIndex((t) => t.key === object.key) && object.key !== 'submissionStatus',
          );
        },
      )
      .addCase(fetchRevenueManagementAccountFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const {
  setRevenueAccountFilterData,
  updateSingleAccount,
  updateRevenueManagementAccountFilters,
  disableRevenueManagementAccountFilterApplied,
  setSortData,
} = revenueSlice.actions;

// Export the reducer
export default revenueSlice.reducer;
